import { Card, Stack, Stepper } from '@mantine/core';
import { useField } from '@mantine/form';
import { IconEye, IconFileDollar, IconSettings, IconUser } from '@tabler/icons-react';
import { CancelButton } from 'components/CancelButton';
import { PageTitle } from 'components/PageTitle';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales/index';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { isNullish } from 'remeda';
import { useLoadPaymentPlanResources } from 'shared/hooks/use-load-payment-plan-resources';

import { type CalculatedSteps, CalculateStepsVariablesSchema, useCalculateSteps } from './hooks';
import { Step1, Step2, Step3, Step4 } from './PaymentPlanWizardComponents';

export type InstallmentType = 'quantity' | 'amount';
export type DelayType = 'days' | 'monthly';

type StepIndex = 0 | 1 | 2 | 3;

export const PaymentPlanWizard = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [activeStep, setActiveStep] = useState<StepIndex>(0);
  const [maximumVisitedStep, _setMaximumVisitedStep] = useState<StepIndex>(0);
  const setMaximumVisitedStep = (step: number) => {
    if (step > maximumVisitedStep) _setMaximumVisitedStep(step as StepIndex);
  };

  const { debtor_id, invoice_ids } = qs.parse(location.search, { arrayFormat: 'comma' });

  // Step 1
  const selectedDebtor = useField<string | null>({ initialValue: (debtor_id as string) ?? null });
  const selectedWorkflow = useField<string | null>({ initialValue: null });

  // Step 2
  const defaultInvoiceIds = (() => {
    if (isNullish(invoice_ids)) return [];
    if (typeof invoice_ids === 'string') return [invoice_ids];
    return invoice_ids as string[];
  })();
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>(defaultInvoiceIds);
  const [selectedCreditNotes, setSelectedCreditNotes] = useState<string[]>([]);
  const [selectedThirdPartyPayments, setSelectedThirdPartyPayments] = useState<string[]>([]);
  const [selectedAccountingPayments, setSelectedAccountingPayments] = useState<string[]>([]);

  // Step 3
  const installmentType = useField<InstallmentType>({ initialValue: 'quantity' });
  const isInstallmentTypeQuantity = installmentType.getValue() === 'quantity';
  const installmentValue = useField<number | string>({ initialValue: 12 });
  const delayType = useField<DelayType>({ initialValue: 'days' });
  const isDelayTypeMonthly = delayType.getValue() === 'monthly';
  const delayDayInMonth = useField<Date>({ initialValue: new Date('01-01-2025') });
  const firstDeadlineDate = useField<Date>({
    initialValue: dayjs().add(1, 'day').toDate(),
  });
  const delayInDays = useField<number | string>({ initialValue: 30 });
  const lateFees = useField<boolean, 'controlled', 'checkbox'>({
    type: 'checkbox',
    initialValue: false,
  });

  // Step 4
  const [paymentSteps, setPaymentSteps] = useState<CalculatedSteps>();
  const { calculateSteps, isCalculateStepsLoading } = useCalculateSteps();

  const paymentStepCalculationData = {
    invoiceIds: selectedInvoices,
    creditNoteIds: selectedCreditNotes,
    accountingPaymentIds: selectedAccountingPayments,
    thirdPartyPaymentIds: selectedThirdPartyPayments,
    deadlineAmount: isInstallmentTypeQuantity ? undefined : installmentValue.getValue(),
    deadlineCount: isInstallmentTypeQuantity ? installmentValue.getValue() : undefined,
    debtorId: selectedDebtor.getValue(),
    dueDate: firstDeadlineDate.getValue().toISOString(),
    withFees: lateFees.getValue(),
    delayType: delayType.getValue(),
    delayInDays: isDelayTypeMonthly ? undefined : delayInDays.getValue(),
    delayDayInMonth: isDelayTypeMonthly ? delayDayInMonth.getValue().getDate() : undefined,
  };

  const parsedPaymentStepCalculationData = CalculateStepsVariablesSchema.safeParse(
    paymentStepCalculationData,
  );
  const isPaymentStepCalculationDataValid = parsedPaymentStepCalculationData.success;

  useEffect(() => {
    setSelectedInvoices([]);
    setSelectedCreditNotes([]);
    setSelectedThirdPartyPayments([]);
    setSelectedAccountingPayments([]);
  }, [selectedDebtor.getValue()]);

  useEffect(() => {
    if (!isPaymentStepCalculationDataValid) {
      console.error(parsedPaymentStepCalculationData.error);
      return;
    }

    calculateSteps(parsedPaymentStepCalculationData.data!, {
      onSuccess: data => setPaymentSteps(data),
    });
  }, [JSON.stringify(parsedPaymentStepCalculationData.data)]);

  const { paymentPlanResources, isPaymentPlanResourcesFetching } = useLoadPaymentPlanResources({
    debtorId: selectedDebtor.getValue(),
  });

  return (
    <Stack>
      <PageTitle>
        {t(i18nKeys.FORM.PAYMENT_PLAN.NEW.TITLE)}
        <PageTitle.Actions>
          <CancelButton
            onClick={() => {
              history.push('/payment-plans');
            }}
          />
        </PageTitle.Actions>
      </PageTitle>

      <Card radius="lg">
        <Stepper
          active={activeStep}
          onStepClick={(index: number) => setActiveStep(index as 0 | 1 | 2 | 3)}
        >
          <Stepper.Step
            allowStepSelect
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEPS.CLIENT_AND_WORKFLOW)}
            icon={<IconUser stroke={1.5} />}
            allowStepClick
          >
            <Step1
              onClickNext={() => setActiveStep(1)}
              debtorField={selectedDebtor}
              workflowField={selectedWorkflow}
            />
          </Stepper.Step>
          <Stepper.Step
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEPS.INVOICES)}
            icon={<IconFileDollar stroke={1.5} />}
            allowStepSelect={maximumVisitedStep >= 1}
          >
            <Step2
              onVisit={() => setMaximumVisitedStep(1)}
              isLoading={isPaymentPlanResourcesFetching}
              resources={paymentPlanResources}
              selectedInvoices={selectedInvoices}
              setSelectedInvoices={setSelectedInvoices}
              selectedCreditNotes={selectedCreditNotes}
              setSelectedCreditNotes={setSelectedCreditNotes}
              selectedThirdPartyPayments={selectedThirdPartyPayments}
              setSelectedPayments={setSelectedThirdPartyPayments}
              selectedAccountingPayments={selectedAccountingPayments}
              setSelectedAccountingPayments={setSelectedAccountingPayments}
              onClickNext={() => setActiveStep(2)}
            />
          </Stepper.Step>
          <Stepper.Step
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEPS.INSTALLMENTS)}
            icon={<IconSettings stroke={1.5} />}
            allowStepSelect={maximumVisitedStep >= 2}
          >
            <Step3
              onVisit={() => setMaximumVisitedStep(2)}
              installmentType={installmentType}
              installmentValue={installmentValue}
              firstDeadlineDate={firstDeadlineDate}
              delayType={delayType}
              delayDayInMonth={delayDayInMonth}
              delayInDays={delayInDays}
              lateFees={lateFees}
              onClickNext={() => setActiveStep(3)}
            />
          </Stepper.Step>
          <Stepper.Step
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEPS.OVERVIEW)}
            icon={<IconEye stroke={1.5} />}
            allowStepSelect={maximumVisitedStep >= 3}
          >
            <Step4
              onVisit={() => setMaximumVisitedStep(3)}
              resources={paymentPlanResources}
              selectedInvoices={selectedInvoices}
              selectedCreditNotes={selectedCreditNotes}
              selectedThirdPartyPayments={selectedThirdPartyPayments}
              selectedAccountingPayments={selectedAccountingPayments}
              paymentSteps={paymentSteps}
              isPaymentStepsLoading={isCalculateStepsLoading}
              installmentType={installmentType}
              installmentValue={installmentValue}
              firstDeadlineDate={firstDeadlineDate}
              debtorField={selectedDebtor}
              workflowField={selectedWorkflow}
              lateFees={lateFees}
              delay={delayInDays}
            />
          </Stepper.Step>
        </Stepper>
      </Card>
    </Stack>
  );
};
